/**
 * LotSmith — Live data provider (M12).
 *
 * Fetches the dashboard from worker-license read endpoints:
 *   GET /v1/accounts             — list user's accounts
 *   GET /v1/accounts/:id/equity  — equity curve points
 *   GET /v1/accounts/:id/trades  — recent trades
 *   GET /v1/accounts/:id/metrics — today's dashboard metrics
 *
 * All four routes are session-cookie authed; on 401 we surface an error so
 * DashboardShell can prompt re-login. EA-status is inferred from the most
 * recent equity point: a HEARTBEAT/TRADE_CLOSE within the last ~5 min →
 * "connected"; otherwise "idle"/"offline" by recency.
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

import type { DemoTrade, EquityPoint } from "../data/demo.js";
import {
  DataCtx,
  type DevAccount,
  type DevCtx,
  type DevMetrics,
  type DevUser,
  type EaStatus,
} from "./dataContext.js";

interface ApiAccount {
  id: string;
  propFirm: string;
  phase: string;
  status: string;
  brokerLogin: string;
  brokerName: string;
  brokerServer: string;
  startBalance: number;
  currentBalance: number;
  currentEquity: number;
  currency: string;
  eaState: string | null;
  eaStateReason: string | null;
  eaStateUpdatedAt: string | null;
}

interface ApiEquityPoint {
  ts: string;
  balance: number;
  equity: number;
  open_pnl: number;
  source: "HEARTBEAT" | "TRADE_CLOSE" | "MANUAL";
}

interface ApiTrade {
  id: string;
  brokerTicket: string;
  symbol: string;
  direction: "LONG" | "SHORT";
  volume: number;
  openPrice: number;
  closePrice: number | null;
  pnl: number | null;
  openTime: string;
  closeTime: string | null;
  state: "OPEN" | "CLOSED";
  note: string | null;
  mood: import("../data/demo.js").Mood | null;
  setupTags: string[] | null;
  mistakeTags: string[] | null;
}

const FRESH_MS = 5 * 60 * 1000;
const STALE_MS = 30 * 60 * 1000;

interface LiveProviderProps {
  apiBase: string;
  user: DevUser;
  children: ReactNode;
}

export function LiveProvider({ apiBase, user, children }: LiveProviderProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accounts, setAccounts] = useState<DevAccount[]>([]);
  const [activeAccountId, setActiveAccountId] = useState<string>("");
  const [metrics, setMetrics] = useState<DevMetrics | null>(null);
  const [equity, setEquity] = useState<EquityPoint[]>([]);
  const [trades, setTrades] = useState<DemoTrade[]>([]);
  const [eaStatus, setEaStatus] = useState<EaStatus>("never-installed");
  const [lastSyncAt, setLastSyncAt] = useState<Date | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const reload = useCallback(() => {
    setReloadKey((n) => n + 1);
  }, []);

  const patchTrade = useCallback(async (
    tradeId: string,
    body: Record<string, unknown>,
    optimistic: (t: DemoTrade) => DemoTrade,
    failureMsg: string,
  ) => {
    if (!activeAccountId) throw new Error("No active account selected.");
    let snapshot: DemoTrade[] = [];
    setTrades((prev) => {
      snapshot = prev;
      return prev.map((t) => (t.id === tradeId ? optimistic(t) : t));
    });
    try {
      const r = await fetch(`${apiBase}/v1/accounts/${activeAccountId}/trades/${tradeId}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "content-type": "application/json", accept: "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error(`patch ${r.status}`);
    } catch (err) {
      setTrades(snapshot);
      throw err instanceof Error ? err : new Error(failureMsg);
    }
  }, [apiBase, activeAccountId]);

  const updateTradeNote = useCallback(async (tradeId: string, note: string | null) => {
    await patchTrade(
      tradeId,
      { note },
      (t) => ({ ...t, note }),
      "Couldn't save the note.",
    );
  }, [patchTrade]);

  const updateTradeJournal = useCallback(async (
    tradeId: string,
    patch: import("./dataContext.js").TradeJournalPatch,
  ) => {
    await patchTrade(
      tradeId,
      patch as Record<string, unknown>,
      (t) => ({
        ...t,
        ...("mood" in patch ? { mood: patch.mood ?? null } : {}),
        ...("setupTags" in patch ? { setupTags: patch.setupTags ?? null } : {}),
        ...("mistakeTags" in patch ? { mistakeTags: patch.mistakeTags ?? null } : {}),
      }),
      "Couldn't save the journal entry.",
    );
  }, [patchTrade]);

  // Step 1 — load account list. Pick first account as active.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        const r = await fetch(`${apiBase}/v1/accounts`, {
          credentials: "include",
          headers: { accept: "application/json" },
        });
        if (cancelled) return;
        if (r.status === 401) {
          setError("You're signed out. Please sign in again.");
          setIsLoading(false);
          return;
        }
        if (!r.ok) throw new Error(`accounts ${r.status}`);
        const j = (await r.json()) as { accounts: ApiAccount[] };
        const list = j.accounts.map(toDevAccount);
        if (cancelled) return;
        setAccounts(list);
        if (list.length === 0) {
          setMetrics(null);
          setEquity([]);
          setTrades([]);
          setEaStatus("never-installed");
          setLastSyncAt(null);
          setIsLoading(false);
          return;
        }
        // Preserve current active id if still valid; otherwise pick first.
        setActiveAccountId((current) => {
          const stillValid = list.some((a) => a.id === current);
          return stillValid && current ? current : list[0]!.id;
        });
      } catch (e) {
        if (!cancelled) setError(messageOf(e));
        setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [apiBase, reloadKey]);

  // Step 2 — when active account changes, load metrics + equity + trades in parallel.
  useEffect(() => {
    if (!activeAccountId) return;
    let cancelled = false;
    // Without this, switching accounts via the dropdown leaves
    // isLoading=false while the new account's metrics/equity/trades
    // are still in flight, so consumer routes (Trades, Calendar,
    // AccountDetail) render the previous account's data for a beat.
    setIsLoading(true);
    setError(null);
    (async () => {
      try {
        const [metricsRes, equityRes, tradesRes] = await Promise.all([
          fetch(`${apiBase}/v1/accounts/${activeAccountId}/metrics`, { credentials: "include" }),
          fetch(`${apiBase}/v1/accounts/${activeAccountId}/equity`, { credentials: "include" }),
          fetch(`${apiBase}/v1/accounts/${activeAccountId}/trades?limit=100`, { credentials: "include" }),
        ]);
        if (cancelled) return;
        if (!metricsRes.ok || !equityRes.ok || !tradesRes.ok) {
          throw new Error(`fetch failed (${metricsRes.status}/${equityRes.status}/${tradesRes.status})`);
        }
        const m = (await metricsRes.json()) as { metrics: DevMetrics };
        const e = (await equityRes.json()) as { points: ApiEquityPoint[] };
        const t = (await tradesRes.json()) as { trades: ApiTrade[] };
        if (cancelled) return;
        setMetrics(m.metrics);
        setEquity(e.points.map(toEquityPoint));
        setTrades(t.trades.map(toDemoTrade));
        const last = e.points.length > 0 ? new Date(e.points[e.points.length - 1]!.ts) : null;
        setLastSyncAt(last);
        setEaStatus(deriveEaStatus(last));
        setIsLoading(false);
      } catch (err) {
        if (!cancelled) {
          setError(messageOf(err));
          setIsLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [apiBase, activeAccountId, reloadKey]);

  const value = useMemo<DevCtx>(() => {
    const activeAccount = accounts.find((a) => a.id === activeAccountId);
    return {
      isLoading,
      error,
      user,
      accounts,
      activeAccountId,
      setActiveAccountId,
      activeAccount,
      metrics,
      equity,
      trades,
      eaStatus,
      lastSyncAt,
      reload,
      updateTradeNote,
      updateTradeJournal,
    };
  }, [isLoading, error, user, accounts, activeAccountId, metrics, equity, trades, eaStatus, lastSyncAt, reload, updateTradeNote, updateTradeJournal]);

  return <DataCtx.Provider value={value}>{children}</DataCtx.Provider>;
}

function toDevAccount(a: ApiAccount): DevAccount {
  return {
    id: a.id,
    propFirm: a.propFirm,
    phase: a.phase,
    brokerLogin: a.brokerLogin,
    currency: a.currency,
    startBalance: a.startBalance,
    currentEquity: a.currentEquity,
    eaState: normalizeEaState(a.eaState),
    eaStateReason: a.eaStateReason,
    eaStateUpdatedAt: a.eaStateUpdatedAt,
  };
}

function normalizeEaState(v: string | null): import("./dataContext.js").EaState | null {
  if (v === "NORMAL" || v === "BLOCKED" || v === "TARGET_HIT" || v === "COOLDOWN" || v === "OVER_LIMIT" || v === "ALGO_OFF") {
    return v;
  }
  return null;
}

function toEquityPoint(p: ApiEquityPoint): EquityPoint {
  return { ts: p.ts, equity: p.equity };
}

function toDemoTrade(t: ApiTrade): DemoTrade {
  return {
    id: t.id,
    ts: t.closeTime ?? t.openTime,
    symbol: t.symbol,
    direction: t.direction,
    volume: t.volume,
    pnl: t.pnl ?? 0,
    note: t.note,
    mood: t.mood ?? null,
    setupTags: t.setupTags ?? null,
    mistakeTags: t.mistakeTags ?? null,
  };
}

function deriveEaStatus(last: Date | null): EaStatus {
  if (!last) return "never-installed";
  const age = Date.now() - last.getTime();
  if (age <= FRESH_MS) return "connected";
  if (age <= STALE_MS) return "idle";
  return "offline";
}

function messageOf(e: unknown): string {
  if (e instanceof Error) return e.message;
  return "Couldn't load account data. Check your connection and try again.";
}
